import { useState, useEffect } from 'react';
import {
  Calendar,
  TrendingUp,
  DollarSign,
  Package,
  Users,
  FileText,
  Download,
  Send,
  CheckCircle,
  Clock
} from 'lucide-react';
import useDashboardStore from '../../store/useDashboardStore';
import useAuthStore from '../../stores/useAuthStore';

/**
 * Daily Report Page - تقرير المندوب اليومي
 *
 * Features:
 * - Today's sales summary
 * - Collections summary
 * - Returns summary
 * - Stock movements
 * - Submit report to admin
 *
 * Protocols:
 * - P-COMPONENT-001: Clean Structure
 * - P-STATE-001: Zustand Integration
 * - P-RTL-001: RTL Support
 * - P-UX-001: User Experience
 */

const DailyReport = () => {
  const { user } = useAuthStore();
  const { statistics, refreshDashboard } = useDashboardStore();

  const [reportDate] = useState(new Date());
  const [reportStatus, setReportStatus] = useState('draft'); // draft, submitted
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  // Load dashboard data on mount
  useEffect(() => {
    refreshDashboard();
  }, [refreshDashboard]);

  // Prepare today's data from statistics
  const todayData = {
    invoicesCount: statistics.todaySales.count || 0,
    collectionsCount: statistics.collections.count || 0,
    returnsCount: statistics.returns.count || 0,
    customersServed: statistics.todayCustomers.count || 0,
    totalSales: parseFloat(statistics.todaySales.total || 0),
    totalCollections: parseFloat(statistics.collections.total || 0),
    totalReturns: parseFloat(statistics.returns.total || 0),
    netSales: parseFloat(statistics.todaySales.total || 0) - parseFloat(statistics.returns.total || 0)
  };

  // Submit report
  const handleSubmitReport = async () => {
    setIsSubmitting(true);

    try {
      // Simulate API call to submit report
      await new Promise(resolve => setTimeout(resolve, 1500));

      // In real app: await api.post('/mobile/daily-report', reportData)

      setReportStatus('submitted');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Submit report error:', error);
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Download report as text
  const handleDownloadReport = () => {
    const reportText = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   التقرير اليومي للمندوب
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

المندوب: ${user?.name || 'غير محدد'}
التاريخ: ${reportDate.toLocaleDateString('ar-EG')}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   ملخص المبيعات
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

عدد الفواتير: ${todayData.invoicesCount}
إجمالي المبيعات: ${todayData.totalSales.toFixed(3)} د.ك
المرتجعات: ${todayData.totalReturns.toFixed(3)} د.ك
صافي المبيعات: ${todayData.netSales.toFixed(3)} د.ك

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   التحصيلات
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

عدد التحصيلات: ${todayData.collectionsCount}
إجمالي المحصل: ${todayData.totalCollections.toFixed(3)} د.ك

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   العملاء
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

عدد العملاء: ${todayData.customersServed}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;

    const blob = new Blob([reportText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `daily-report-${reportDate.toISOString().split('T')[0]}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white p-6">
        <div className="flex items-center gap-3 mb-2">
          <FileText className="w-8 h-8" />
          <div>
            <h1 className="text-2xl font-bold">التقرير اليومي</h1>
            <p className="text-blue-100 text-sm">
              {reportDate.toLocaleDateString('ar-EG', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>

        {/* Status Badge */}
        <div className="mt-4">
          {reportStatus === 'submitted' ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 rounded-full text-sm font-medium">
              <CheckCircle className="w-4 h-4" />
              <span>تم الإرسال</span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 rounded-full text-sm font-medium">
              <Clock className="w-4 h-4" />
              <span>مسودة</span>
            </div>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4">
          {/* Sales Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <p className="text-sm text-gray-600 dark:text-gray-400">المبيعات</p>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {todayData.invoicesCount}
            </p>
            <p className="text-xs text-gray-500">فاتورة</p>
          </div>

          {/* Collections Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <p className="text-sm text-gray-600 dark:text-gray-400">التحصيلات</p>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {todayData.collectionsCount}
            </p>
            <p className="text-xs text-gray-500">عملية</p>
          </div>

          {/* Returns Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-5 h-5 text-orange-600" />
              <p className="text-sm text-gray-600 dark:text-gray-400">المرتجعات</p>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {todayData.returnsCount}
            </p>
            <p className="text-xs text-gray-500">مرتجع</p>
          </div>

          {/* Customers Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-purple-600" />
              <p className="text-sm text-gray-600 dark:text-gray-400">العملاء</p>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {todayData.customersServed}
            </p>
            <p className="text-xs text-gray-500">عميل</p>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            الملخص المالي
          </h2>

          <div className="space-y-3">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">إجمالي المبيعات</span>
              <span className="font-bold text-gray-900 dark:text-white">
                {todayData.totalSales.toFixed(3)} د.ك
              </span>
            </div>

            <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">المرتجعات</span>
              <span className="font-bold text-orange-600">
                - {todayData.totalReturns.toFixed(3)} د.ك
              </span>
            </div>

            <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">صافي المبيعات</span>
              <span className="font-bold text-blue-600">
                {todayData.netSales.toFixed(3)} د.ك
              </span>
            </div>

            <div className="flex items-center justify-between pt-2">
              <span className="text-gray-600 dark:text-gray-400">التحصيلات النقدية</span>
              <span className="font-bold text-green-600">
                {todayData.totalCollections.toFixed(3)} د.ك
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Submit Report Button */}
          {reportStatus === 'draft' && (
            <button
              onClick={handleSubmitReport}
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  <span>جاري الإرسال...</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>إرسال التقرير للإدارة</span>
                </>
              )}
            </button>
          )}

          {/* Download Report Button */}
          <button
            onClick={handleDownloadReport}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-xl font-medium transition-all duration-300"
          >
            <Download className="w-5 h-5" />
            <span>تحميل نسخة نصية</span>
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            💡 <strong>نصيحة:</strong> يُفضل إرسال التقرير اليومي في نهاية يوم العمل لمتابعة دقيقة من الإدارة.
          </p>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-6 text-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <h3 className="text-2xl font-bold text-white">تم بنجاح!</h3>
            </div>

            {/* Body */}
            <div className="p-6 text-center">
              <p className="text-gray-700 dark:text-gray-300 text-lg mb-2">
                تم إرسال التقرير اليومي للإدارة
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                سيتم مراجعة التقرير من قبل الإدارة قريباً
              </p>
            </div>

            {/* Footer */}
            <div className="p-6 pt-0">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                حسناً
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-red-500 to-rose-500 p-6 text-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <svg className="w-12 h-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white">فشل الإرسال</h3>
            </div>

            {/* Body */}
            <div className="p-6 text-center">
              <p className="text-gray-700 dark:text-gray-300 text-lg mb-2">
                فشل إرسال التقرير
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                يرجى المحاولة مرة أخرى أو التواصل مع الدعم الفني
              </p>
            </div>

            {/* Footer */}
            <div className="p-6 pt-0">
              <button
                onClick={() => setShowErrorModal(false)}
                className="w-full py-3 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                حسناً
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyReport;
